<template>
  <div class="text-center">
    <p class="balance mb-8 text-lg">
      <slot name="title">
        {{ title }}
      </slot>
    </p>

    <div
      class="mask-l-from-90% mask-r-from-90% h-(--height) relative overflow-hidden"
      :style="{
        '--height': `${logoHeight}px`,
      }"
    >
      <ul
        ref="carousel"
        class="carousel absolute flex w-max items-center gap-8"
        :style="carouselStyle"
      >
        <li
          v-for="(item, index) in [...list, ...list]"
          :key="`${item.name}_${index}`"
          :aria-hidden="index >= list.length"
        >
          <app-image
            :alt="`${item.name} logo`"
            class="max-sm:max-h-8"
            :class="logoClasses"
            :height="logoHeight"
            loading="eager"
            :src="item.src"
            :style="{ maxWidth: `${2.4 * logoHeight}px` }"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { I18nMessage } from "@pollen/core/model/i18n.model";
import capitalize from "lodash/capitalize";

import type { BrandLogoCarouselShareableProps } from "~/business-areas/brand/brand.model";

const properties = withDefaults(
  defineProps<
    {
      items: string[];
      title?: I18nMessage;
      logoHeight?: number;
    } & BrandLogoCarouselShareableProps
  >(),
  {
    title: undefined,
    logoHeight: 40,
  },
);

const list = computed(() => {
  return properties.items.map((item) => {
    return {
      src: `/images/logos/black-and-white/${item}.svg`,
      name: capitalize(item),
    };
  });
});

const carousel = useTemplateRef("carousel");

const { width: carouselWidth } = useElementSize(carousel);

const PIXELS_PER_SECOND = 20;

const carouselStyle = computed(() => {
  return {
    animationDuration: `${(carouselWidth.value / 2 / PIXELS_PER_SECOND).toFixed(2)}s`,
  };
});
</script>

<style scoped>
@keyframes carousel {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-50%);
  }
}

.carousel {
  animation-name: carousel;
  animation-iteration-count: infinite;
  animation-timing-function: linear;

  &.paused {
    animation-play-state: paused;
  }
}
</style>
